import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Alert, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from '../components/rn';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import PrimaryActionButton from '../components/PrimaryActionButton';
import ProfileSection from '../components/ProfileSection';
import { useTranslation } from '../i18n/I18nContext';
import { normalizeInputDigits } from '../i18n/inputDigits';
import { useTheme } from '../theme/ThemeContext';
import { profileFieldStyle } from '../theme/profileSection';
import { fontSize, spacing } from '../theme';
import { api, ApiError, type PasskeyRecovery, type PasskeySummary } from '../services/api';
import { passkeysAvailable } from '../passkeys/authenticator';
import { addPasskey, cancelPasskeyRecovery, recoverPasskey, removePasskey, type TapPrompt } from '../passkeys/passkeyFlow';

/**
 * 9.33.9.3 — Profile → Sign-in methods → Passkeys (D-780).
 *
 * Lists the member's passkeys, adds one and removes one. The first passkey needs
 * the registered number confirmed again by SMS; a further one, and every removal,
 * needs an existing passkey. The server enforces all of it.
 */
type Phase = 'list' | 'reauth' | 'code';
/** What the SMS step confirms: adding, starting a reset (D-876), or finishing one. */
type Intent = 'add' | 'startRecovery' | 'finishRecovery';

export default function PasskeysScreen({ token, onBack, onAdded }: {
  token: string;
  onBack: () => void;
  /** Called after a passkey was added, e.g. to return to the chat that asked for it. */
  onAdded?: () => void;
}) {
  const { t, lang } = useTranslation();
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null);
  const [taskChannel, setTaskChannel] = useState(false);
  const [signIn, setSignIn] = useState(false);
  const [recovery, setRecovery] = useState<PasskeyRecovery | null>(null);
  const [intent, setIntent] = useState<Intent>('add');
  const [loadFailed, setLoadFailed] = useState(false);
  const [phase, setPhase] = useState<Phase>('list');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [phoneHint, setPhoneHint] = useState('');
  const [code, setCode] = useState('');
  const attempt = useRef<string | null>(null);
  const reauth = useRef<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const available = passkeysAvailable();
  const prompt: TapPrompt = { title: t('passkeys.tapTitle'), action: t('passkeys.tapAction'), cancel: t('common.cancel') };

  const load = useCallback(async () => {
    try {
      const list = await api.listPasskeys(token);
      if (!alive.current) return;
      setPasskeys(list.passkeys);
      setTaskChannel(typeof list.taskChannelReceiverId === 'number');
      setRecovery(list.recovery ?? null);
      setSignIn(list.signIn === true);
      setLoadFailed(false);
    } catch {
      if (alive.current) setLoadFailed(true);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const failureText = (reason: string): string | null =>
    reason === 'cancelled' ? null
      : reason === 'exists' ? t('passkeys.exists')
      : reason === 'unsupported' ? t(Platform.OS === 'web' ? 'passkeys.unsupportedWeb' : 'passkeys.unsupportedApp')
      : t('passkeys.failed');

  async function add() {
    setBusy(true);
    setNotice(null);
    try {
      const result = await addPasskey(token, reauth.current, prompt, passkeys?.length ?? 0);
      if (!alive.current) return;
      if (result === 'reauth') { reauth.current = null; setIntent('add'); setPhase('reauth'); return; }
      if (result === 'added') {
        reauth.current = null;
        setPhase('list');
        setNotice(t('passkeys.added'));
        await load();
        onAdded?.();
        return;
      }
      setNotice(failureText(result));
    } catch {
      if (alive.current) setNotice(t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function sendCode() {
    setBusy(true);
    setNotice(null);
    setCode('');
    try {
      const started = await api.startPasskeyReauth(token);
      if (!alive.current) return;
      attempt.current = started.attempt;
      setPhoneHint(started.phoneHint);
      setPhase('code');
    } catch (error) {
      if (alive.current) setNotice(error instanceof ApiError && error.status === 429 ? t('otp.tooManyAttempts') : t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function verify(entered: string) {
    const current = attempt.current;
    if (!current) return;
    setBusy(true);
    setNotice(null);
    try {
      await api.completePasskeyReauth(token, current, entered);
      reauth.current = current;
      attempt.current = null;
    } catch (error) {
      attempt.current = null;
      if (!alive.current) return;
      setBusy(false);
      setPhase('reauth');
      setNotice(error instanceof ApiError && error.status === 429 ? t('otp.tooManyAttempts')
        : error instanceof ApiError && error.status === 400 ? t('passkeys.wrongCode') : t('passkeys.failed'));
      return;
    }
    if (!alive.current) return;
    setPhase('list');
    if (intent === 'startRecovery') await startRecovery();
    else if (intent === 'finishRecovery') await finishRecovery();
    else await add();
  }

  // D-876 — a lost passkey: the SMS step starts a 48-hour wait, and after it adds the
  // passkey that replaces the lost ones.
  function beginSms(next: Intent) {
    reauth.current = null;
    setNotice(null);
    setIntent(next);
    setPhase('reauth');
  }

  async function startRecovery() {
    const proof = reauth.current;
    reauth.current = null;
    if (!proof) return;
    setBusy(true);
    try {
      await api.requestPasskeyRecovery(token, proof);
      if (!alive.current) return;
      await load();
    } catch {
      if (alive.current) setNotice(t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function finishRecovery() {
    const proof = reauth.current;
    reauth.current = null;
    if (!proof) return;
    setBusy(true);
    try {
      const result = await recoverPasskey(token, proof, prompt);
      if (!alive.current) return;
      if (result === 'reauth') { beginSms('finishRecovery'); return; }
      if (result === 'added') { setNotice(t('passkeys.recoveryDone')); await load(); onAdded?.(); return; }
      setNotice(failureText(result));
    } catch {
      if (alive.current) setNotice(t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function stopRecovery() {
    setBusy(true);
    setNotice(null);
    try {
      const result = await cancelPasskeyRecovery(token, prompt);
      if (!alive.current) return;
      if (result === 'stopped') { setNotice(t('passkeys.recoveryCancelled')); await load(); return; }
      setNotice(failureText(result));
    } catch {
      if (alive.current) setNotice(t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  function confirmRemove(passkey: PasskeySummary) {
    Alert.alert(t('passkeys.removeTitle'), t('passkeys.removeBody'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('passkeys.remove'), style: 'destructive', onPress: () => { remove(passkey.id); } },
    ]);
  }

  async function remove(id: string) {
    setBusy(true);
    setNotice(null);
    try {
      const result = await removePasskey(token, id, prompt);
      if (!alive.current) return;
      if (result === 'removed') { setNotice(t('passkeys.removed')); await load(); return; }
      setNotice(failureText(result));
    } catch {
      if (alive.current) setNotice(t('passkeys.failed'));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  const dateTime = (value: string) => new Date(value).toLocaleString(lang, { dateStyle: 'medium', timeStyle: 'short' });
  const date = (value: string) => new Date(value).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' });

  return <View style={[styles.screen, { backgroundColor: colors.white }]}>
    <PageHeader title={t('passkeys.title')} onBack={onBack} floating />
    <KeyboardAvoidingView style={styles.screen} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView keyboardShouldPersistTaps="handled" testID="passkeys-scroll"
        contentContainerStyle={[styles.content, { paddingTop: screenHeaderContentInset(insets.top), paddingBottom: insets.bottom + spacing.xl }]}>
        <Text style={[styles.text, styles.inset, { color: colors.gray600 }]}>{t('passkeys.intro')}</Text>
        {signIn && <Text testID="passkeys-sign-in-intro" style={[styles.text, styles.inset, { color: colors.gray600 }]}>{t('passkeys.signInIntro')}</Text>}
        {taskChannel && <Text testID="passkeys-task-intro" style={[styles.text, styles.inset, { color: colors.gray600 }]}>{t('passkeys.taskIntro')}</Text>}
        {!available && <Text testID="passkeys-unsupported" style={[styles.text, styles.inset, { color: colors.black }]}>
          {t(Platform.OS === 'web' ? 'passkeys.unsupportedWeb' : 'passkeys.unsupportedApp')}</Text>}

        {phase === 'list' && <>
          {passkeys === null && !loadFailed && <Text style={[styles.text, styles.inset, { color: colors.gray600 }]}>{t('common.loading')}</Text>}
          {loadFailed && <PrimaryActionButton testID="passkeys-retry" style={styles.inset} label={t('common.retry')} onPress={() => { load(); }} />}
          {passkeys !== null && passkeys.length === 0 && <Text testID="passkeys-empty" style={[styles.text, styles.inset, { color: colors.black }]}>{t('passkeys.empty')}</Text>}
          {passkeys !== null && passkeys.length > 0 && <ProfileSection testID="passkeys-list" separated>
            {passkeys.map((passkey) => <View key={passkey.id} testID={`passkey-${passkey.id}`} style={styles.row}>
              <View style={styles.rowText}>
                <Text style={[styles.label, { color: colors.black }]}>{passkey.label || t('passkeys.unnamed')}</Text>
                <Text style={[styles.detail, { color: colors.gray600 }]}>
                  {passkey.lastUsedAt ? t('passkeys.lastUsed', { date: date(passkey.lastUsedAt) }) : t('passkeys.addedOn', { date: date(passkey.createdAt) })}
                </Text>
              </View>
              {available && <TouchableOpacity testID={`passkey-remove-${passkey.id}`} accessibilityRole="button"
                accessibilityLabel={`${t('passkeys.remove')} ${passkey.label || t('passkeys.unnamed')}`}
                disabled={busy} onPress={() => confirmRemove(passkey)} style={styles.remove}>
                <Text style={[styles.removeText, busy && styles.dim]}>{t('passkeys.remove')}</Text>
              </TouchableOpacity>}
            </View>)}
          </ProfileSection>}
          {available && passkeys !== null && !recovery && <PrimaryActionButton testID="passkeys-add" style={styles.inset} label={t('passkeys.add')}
            loading={busy} onPress={() => { add(); }} />}
          {recovery && !recovery.ready && <>
            <Text testID="passkeys-recovery-pending" style={[styles.text, styles.inset, { color: colors.black }]}>
              {t('passkeys.recoveryPending', { date: dateTime(recovery.readyAt) })}</Text>
            {available && <PrimaryActionButton testID="passkeys-recovery-cancel" style={styles.inset}
              label={t('passkeys.recoveryCancel')} loading={busy} onPress={() => { stopRecovery(); }} />}
          </>}
          {recovery?.ready && <>
            <Text testID="passkeys-recovery-ready" style={[styles.text, styles.inset, { color: colors.black }]}>{t('passkeys.recoveryReady')}</Text>
            {available && <PrimaryActionButton testID="passkeys-recovery-finish" style={styles.inset}
              label={t('passkeys.add')} loading={busy} onPress={() => beginSms('finishRecovery')} />}
          </>}
          {available && passkeys !== null && passkeys.length > 0 && !recovery && <TouchableOpacity testID="passkeys-lost"
            accessibilityRole="button" disabled={busy} onPress={() => beginSms('startRecovery')} style={[styles.inset, styles.link]}>
            <Text style={[styles.linkText, { color: colors.gray600 }]}>{t('passkeys.lost')}</Text>
          </TouchableOpacity>}
        </>}

        {phase === 'reauth' && <>
          <Text testID="passkeys-reauth" style={[styles.text, styles.inset, { color: colors.black }]}>
            {t(intent === 'startRecovery' ? 'passkeys.recoveryIntro' : 'passkeys.reauthIntro')}</Text>
          <PrimaryActionButton testID="passkeys-send-code" style={styles.inset} label={t('passkeys.sendCode')} loading={busy} onPress={() => { sendCode(); }} />
          {intent !== 'add' && <PrimaryActionButton testID="passkeys-back-to-list" style={styles.inset} label={t('common.cancel')}
            disabled={busy} onPress={() => { setIntent('add'); setPhase('list'); }} />}
        </>}

        {phase === 'code' && <>
          <Text testID="passkeys-code-sent" accessibilityLiveRegion="polite" style={[styles.text, styles.inset, { color: colors.black }]}>
            {t('passkeys.codeSent', { phone: phoneHint })}</Text>
          <TextInput testID="passkeys-code" accessibilityLabel={t('passkeys.codeLabel')}
            style={[profileFieldStyle(colors), styles.code, styles.inset, { color: colors.black }]}
            value={code} onChangeText={(value) => setCode(normalizeInputDigits(value, lang).replace(/[^0-9]/g, '').slice(0, 10))}
            editable={!busy} keyboardType="number-pad" textContentType="oneTimeCode" autoComplete="sms-otp"
            maxLength={10} autoFocus />
          <PrimaryActionButton testID="passkeys-verify" style={styles.inset} label={t('otp.verify')}
            disabled={!/^\d{4,10}$/.test(code)} loading={busy}
            onPress={() => { const entered = code; setCode(''); verify(entered); }} />
          <PrimaryActionButton testID="passkeys-start-again" style={styles.inset} label={t('passkeys.startAgain')}
            disabled={busy} onPress={() => { attempt.current = null; setCode(''); setPhase('reauth'); }} />
        </>}

        {notice && <Text testID="passkeys-notice" accessibilityLiveRegion="polite" style={[styles.text, styles.inset, { color: colors.gray600 }]}>{notice}</Text>}
      </ScrollView>
    </KeyboardAvoidingView>
  </View>;
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  // The list's section frame keeps its own side margin, as on Sign-in methods.
  content: { paddingVertical: spacing.lg, gap: spacing.md },
  inset: { marginHorizontal: spacing.lg },
  text: { fontSize: fontSize.md, lineHeight: 24, flexShrink: 1 },
  code: { fontSize: fontSize.md },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, minHeight: 64, paddingVertical: spacing.md, paddingHorizontal: spacing.sm },
  rowText: { flex: 1, minWidth: 0 },
  label: { fontSize: fontSize.md, fontWeight: '500' },
  detail: { fontSize: fontSize.sm, marginTop: 2 },
  remove: { minHeight: 44, justifyContent: 'center', paddingHorizontal: spacing.sm },
  removeText: { fontSize: fontSize.md, color: '#D92D20' },
  dim: { opacity: 0.5 },
  link: { minHeight: 44, justifyContent: 'center' },
  linkText: { fontSize: fontSize.md, textDecorationLine: 'underline' },
});
